import {
  type Action,
  type ActionKind,
  type FloorKind,
  type Problem,
  problemNeedsYou,
  serviceLabel,
} from "@pagerless/schema";
import { useState } from "react";
import { Link, useParams } from "react-router";
import { ApiError, type ProblemView } from "../api";
import { ScreenHead } from "../components/ScreenHead";
import { ErrorNote, Loading, useShell } from "../components/Shell";
import { useDeps, useResource } from "../context";
import { deviceLabel, savePasskeyHere } from "../lib/devicePasskey";
import { clock, KIND_VERB, problemPhase, shortClock } from "../lib/format";
import { type FixedBy, problemPager } from "../lib/pager";
import { PasskeyCancelled, PasskeyUnavailable } from "../lib/passkey";
import { serviceHref } from "./Services";

const FLOOR_PHRASE: Record<FloorKind, string> = {
  payments_failing: "payments break",
  app_silent: "your app goes quiet",
  crash_loop: "your app keeps crashing",
  http_5xx: "pages start failing",
};

const TEACH_PHRASE: Partial<Record<ActionKind, string>> = {
  undo_update: "undo a bad update",
  restart: "restart what's stuck",
  add_capacity: "add capacity",
  pause: "pause the app",
  open_pr: "write a fix for review",
};

const YES_LABEL: Record<ActionKind, string> = {
  undo_update: "Yes, undo it",
  restart: "Yes, restart it",
  add_capacity: "Yes, add capacity",
  pause: "Yes, pause it",
  open_pr: "Yes, open the fix",
  email_customers: "Yes, send it",
  data_change: "Yes, do it",
  custom: "Yes, do it",
};

type ApproveState =
  | { s: "idle" }
  | { s: "asking" }
  | { s: "signing" }
  | { s: "sending" }
  /** The yes went through (or the fix was already under way): the button stays off for good. */
  | { s: "sent"; note: string | null }
  | { s: "error"; message: string; offerSave?: boolean }
  /** No passkey for this account on this device: offer to save one here, then retry (bug d). */
  | { s: "no_passkey" }
  /** This browser can't do passkeys at all: only the phone can approve. */
  | { s: "unsupported" }
  | { s: "saving" };

const ACTIVE: Action["state"][] = ["approved", "running", "verifying"];

export function ProblemPage() {
  const { id = "" } = useParams();
  const view = useResource((api) => api.problem(id), id);
  const { refreshBadge } = useShell();

  if (view.status === "loading") return <Loading what="Loading this problem" />;
  if (view.status === "error") return <ErrorNote error={view.error} retry={view.reload} />;
  const reload = () => {
    view.reload();
    refreshBadge();
  };
  return <ProblemBody view={view.data} reload={reload} />;
}

const PHASE_WORDS = {
  needs_you: "waiting for you",
  on_it: "you're on it",
  fixing: "being fixed",
  watching: "helper is watching",
  fixed: "fixed",
} as const;

function ProblemBody({ view, reload }: { view: ProblemView; reload: () => void }) {
  const { problem, actions, timeline, appName } = view;
  const waiting = actions.find((a) => a.state === "awaiting_approval") ?? null;
  const running = actions.find((a) => ACTIVE.includes(a.state)) ?? null;
  const fixFailed = actions.some((a) => a.state === "failed");
  const phase = problemPhase(problem, waiting !== null, needsPerson(view));
  const humanApproved = actions.find((a) => a.approval !== null && a.state === "done");
  const fixedBy = whoFixed(view);
  const facts = `${appName} · ${clock(problem.openedAt)} · ${PHASE_WORDS[phase]}${
    phase === "fixed" && problem.resolvedAt ? ` ${clock(problem.resolvedAt)}` : ""
  }`;
  // The service it came from opens that service's signals.
  const sub = problem.service ? (
    <>
      {facts}{" "}
      <Link to={serviceHref(problem.projectId, problem.service)} className="chip">
        {serviceLabel(problem.service)}
      </Link>
    </>
  ) : (
    facts
  );

  return (
    <>
      {/* The tab says what the headline says (D2). */}
      <title>{`${phase === "fixed" ? fixedTitle(problem) : problem.title} · Pagerless`}</title>
      <ScreenHead
        pager={problemPager({ problem, phase, waiting, running, fixedBy, fixFailed })}
        title={phase === "fixed" ? fixedTitle(problem) : problem.title}
        sub={sub}
        live
      >
        {phase === "needs_you" && waiting && <AskBox action={waiting} problemId={problem.id} onDone={reload} />}
        {phase === "needs_you" && !waiting && <TakeOver problemId={problem.id} fixFailed={fixFailed} onDone={reload} />}
        {phase === "on_it" && (
          <p className="muted" role="status" style={{ margin: 0 }}>
            You took it{problem.ackedAt ? ` at ${clock(problem.ackedAt)}` : ""}. Calls stopped; helper won't act alone.
          </p>
        )}
        {phase === "fixing" && <Fixing running={running} />}
        {phase === "fixed" && <Link to="/home">Back to home</Link>}
        {phase === "watching" && (
          <p className="muted" role="status" style={{ margin: 0 }}>
            {actions.some((a) => a.state === "rejected")
              ? "You said not now. We'll ask again if it gets worse."
              : running
                ? "Your helper is fixing this now."
                : "Nothing needs you yet."}
          </p>
        )}
      </ScreenHead>
      {phase === "fixed" ? (
        <FixedView view={view} humanApproved={humanApproved ?? null} />
      ) : (
        // One column: what/why/fix, then the timeline. No half-empty card beside a long one (bug 13).
        <>
          <Explain view={view} />
          <div style={{ marginTop: "var(--s4)" }}>
            <Timeline items={withoutRepeats(timeline, problem.diagnosis?.why ?? null)} />
          </div>
        </>
      )}
    </>
  );
}

/**
 * Open with no yes/no to give, but someone has to act: a fix the helper ran failed, or we paged a person
 * (and they haven't said "not now"). Then the page says it needs you, never "helper is watching" (bug 11).
 */
export function needsPerson(view: ProblemView): boolean {
  // The api sends needsYou; an older one doesn't, so compute it the same way (shared rule in the schema).
  return view.needsYou || problemNeedsYou(view);
}

/** The timeline repeats the diagnosis as "Likely cause: …"; the card above already says why (bug 13). */
export function withoutRepeats(items: ProblemView["timeline"], why: string | null): ProblemView["timeline"] {
  return why === null ? items : items.filter((i) => !(i.text.startsWith("Likely cause") || i.text === why));
}

function TakeOver({ problemId, fixFailed, onDone }: { problemId: string; fixFailed: boolean; onDone: () => void }) {
  const { api } = useDeps();
  const [st, setSt] = useState<"idle" | "busy" | { error: string }>("idle");
  async function take() {
    setSt("busy");
    try {
      await api.ack(problemId);
      onDone();
    } catch (e) {
      setSt({ error: e instanceof ApiError ? e.message : "That didn't go through. Try again." });
    }
  }
  return (
    <section className="ask" aria-labelledby="take-h">
      <p id="take-h">
        <b>{fixFailed ? "Your helper's fix didn't work." : "We paged you."}</b> Nothing to approve: someone needs to
        look.
      </p>
      <div className="btn-row" style={{ marginBottom: 0 }}>
        <button type="button" className="btn hot" onClick={() => void take()} disabled={st === "busy"}>
          {st === "busy" ? "Telling your helper…" : "I'll handle it"}
        </button>
      </div>
      {typeof st === "object" && (
        <p className="err" role="alert">
          {st.error}
        </p>
      )}
    </section>
  );
}

const FIXED_TITLE: Record<FloorKind, string> = {
  payments_failing: "Payments fixed",
  app_silent: "Your app is back",
  crash_loop: "Your app stopped crashing",
  http_5xx: "Pages working again",
};

/** Once fixed, the headline says so, so it never contradicts the pager (bug g). */
export function fixedTitle(problem: Problem): string {
  return problem.decision.floor ? FIXED_TITLE[problem.decision.floor] : `Fixed: ${problem.title}`;
}

/**
 * Who closed it. A person's effort is one tap; the number is how long after the page they tapped
 * (from when we first reached them, else when it opened). Otherwise the helper's own fix, or a hand close.
 */
export function whoFixed(view: ProblemView): FixedBy {
  const yes = view.actions.find((a) => a.approval !== null && a.state === "done");
  if (yes?.approval) {
    const pagedAt = view.pagedAt ?? view.timeline.find((t) => t.hot)?.at ?? view.problem.openedAt;
    return { by: "you", answeredMs: yes.approval.at - pagedAt };
  }
  return view.actions.some((a) => a.state === "done" && a.approval === null) ? { by: "helper" } : { by: "hand" };
}

function Explain({ view }: { view: ProblemView }) {
  const { problem, actions } = view;
  const d = problem.diagnosis;
  const proposed = actions.find((a) => a.state === "awaiting_approval" || a.state === "proposed");
  const effect = proposed && typeof proposed.params.effect === "string" ? proposed.params.effect : null;
  return (
    <div className="box plain pad">
      <p>
        <b>What:</b> {d?.what ?? `${problem.eventCount.toLocaleString("en-US")} errors so far.`}
      </p>
      <p>
        <b>Why:</b> {d?.why ?? "Still working it out."}
      </p>
      {proposed && (
        <p>
          <b>Fix:</b> {proposed.title}.{effect ? ` ${effect}` : ""}
          {proposed.reversible ? " Can be reversed." : " Can't be undone."}
        </p>
      )}
    </div>
  );
}

export function AskBox({ action, problemId, onDone }: { action: Action; problemId: string; onDone: () => void }) {
  const { api, passkey, now } = useDeps();
  const me = useResource((a) => a.me());
  const [st, setSt] = useState<ApproveState>({ s: "idle" });
  const [other, setOther] = useState<null | "handle" | "notnow">(null);
  const busy =
    st.s === "asking" ||
    st.s === "signing" ||
    st.s === "sending" ||
    st.s === "sent" ||
    st.s === "saving" ||
    other !== null;

  async function saveThenRetry() {
    if (me.status !== "ok") return;
    setSt({ s: "saving" });
    try {
      await savePasskeyHere(api, passkey, me.data.person.id);
      me.reload();
    } catch (e) {
      setSt({
        s: "error",
        message:
          e instanceof PasskeyCancelled
            ? "Saving the passkey was cancelled. Nothing was changed."
            : e instanceof ApiError
              ? e.message
              : "We couldn't save a passkey here. Approve from your phone.",
        offerSave: true,
      });
      return;
    }
    await yes(true);
  }

  async function yes(force = false) {
    if (busy && !force) return;
    setSt({ s: "asking" });
    try {
      const challenge = await api.requestApproval(action.id);
      setSt({ s: "signing" });
      const proof = await passkey.approve(challenge, now());
      setSt({ s: "sending" });
      await api.approve(action.id, proof);
      // Never back to idle: the page reloads into "fixing", and a second yes must not be sent.
      setSt({ s: "sent", note: null });
      onDone();
    } catch (e) {
      // Someone (or another device) already said yes: nothing went wrong, show where it is.
      if (e instanceof ApiError && (e.code === "already_running" || e.code === "already_done")) {
        setSt({ s: "sent", note: e.message });
        onDone();
        return;
      }
      if (e instanceof PasskeyUnavailable) {
        setSt({ s: "unsupported" });
        return;
      }
      // "Cancelled" for a person with no passkey at all means the browser had nothing to offer; a
      // signature the server can't match means this device's passkey isn't one for this account.
      // Retrying can never work in either case, so offer to save one here (bug d).
      const hasPasskey = me.status === "ok" ? me.data.hasPasskey : true;
      if (
        (e instanceof PasskeyCancelled && !hasPasskey) ||
        (e instanceof ApiError && (e.code === "passkey_unknown" || e.code === "proof_invalid"))
      ) {
        setSt({ s: "no_passkey" });
        return;
      }
      setSt(
        e instanceof PasskeyCancelled
          ? // The browser can't tell a cancel from "no passkey here", so offer both ways forward.
            { s: "error", message: "Face ID was cancelled. Nothing was changed.", offerSave: true }
          : {
              s: "error",
              message: e instanceof ApiError ? e.message : "We couldn't send your yes. Nothing was changed. Try again.",
            },
      );
    }
  }

  async function handleIt() {
    setOther("handle");
    try {
      await api.reject(action.id);
      await api.ack(problemId);
      onDone();
    } catch (e) {
      setOther(null);
      setSt({ s: "error", message: e instanceof ApiError ? e.message : "That didn't go through. Try again." });
    }
  }

  async function notNow() {
    setOther("notnow");
    try {
      await api.reject(action.id);
      onDone();
    } catch (e) {
      setOther(null);
      setSt({ s: "error", message: e instanceof ApiError ? e.message : "That didn't go through. Try again." });
    }
  }

  const title = `${action.title}?`;
  return (
    <section className="ask" aria-labelledby="ask-h">
      <p id="ask-h">
        <b>{title}</b>
      </p>
      <div className="btn-row" style={{ margin: "8px 0" }}>
        <button type="button" className="btn hot" onClick={() => void yes()} disabled={busy}>
          {st.s === "asking"
            ? "Getting ready…"
            : st.s === "signing"
              ? "Waiting for Face ID…"
              : st.s === "sending"
                ? "Sending…"
                : st.s === "sent"
                  ? "Sent. Fixing now…"
                  : st.s === "saving"
                    ? "Saving a passkey…"
                    : YES_LABEL[action.kind]}
        </button>
        <button type="button" className="btn alt" onClick={handleIt} disabled={busy}>
          {other === "handle" ? "Telling your helper…" : "I'll handle it"}
        </button>
        <button type="button" className="btn alt" onClick={notNow} disabled={busy}>
          Not now
        </button>
      </div>
      {st.s === "error" && (
        <p className="err" role="alert">
          {st.message}
          {st.offerSave && (
            <>
              {" "}
              No passkey on this device?{" "}
              <button type="button" className="link-btn" onClick={() => void saveThenRetry()}>
                Save one now
              </button>
            </>
          )}
        </p>
      )}
      {st.s === "sent" && st.note && (
        <p className="muted" role="status">
          {st.note}
        </p>
      )}
      {st.s === "no_passkey" && (
        <div className="err" role="alert">
          <b>{deviceLabel(typeof navigator === "undefined" ? "" : navigator.userAgent)} has no passkey for you.</b>{" "}
          <button type="button" className="btn sm" onClick={() => void saveThenRetry()}>
            Save one now
          </button>{" "}
          or approve from your phone.
        </div>
      )}
      {st.s === "unsupported" && (
        <p className="err" role="alert">
          This browser can't use a passkey. Approve from your phone.
        </p>
      )}
      <p className="note" style={{ margin: 0 }}>
        {action.policy === "ask" ? `Your rule: ask before ${KIND_VERB[action.kind]}` : "Needs your yes"} · Face ID
      </p>
    </section>
  );
}

function Fixing({ running }: { running: Action | null }) {
  return (
    <p className="muted" role="status" style={{ margin: 0 }}>
      <b>{running ? running.title : "Fixing now"}</b>
      {running?.state === "verifying" ? " · done, checking it holds" : " · in progress"}
    </p>
  );
}

function Timeline({ items }: { items: ProblemView["timeline"] }) {
  return (
    <section className="box tline" aria-labelledby="tl-h">
      <h2 id="tl-h" className="h3" style={{ marginTop: 0 }}>
        What happened
      </h2>
      <ol>
        {items.map((i) => (
          <li key={`${i.at}-${i.text}`} className={i.hot ? "hot" : undefined}>
            <time className="when" dateTime={new Date(i.at).toISOString()}>
              {shortClock(i.at)}
            </time>
            <span>{i.text}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}

/** Screen 9: fixed. The pager has the summary; then what's still waiting, and the teach-the-rule prompt. */
/** Screen 9: fixed. One column like the open state: what happened, the teach prompt, follow-ups, timeline (C4). */
function FixedView({ view, humanApproved }: { view: ProblemView; humanApproved: Action | null }) {
  const { problem, actions, timeline } = view;
  const followUps = actions.filter((a) => a.state === "proposed" || a.state === "awaiting_approval");

  return (
    <div className="stack">
      {problem.summary && (
        <p className="muted" style={{ margin: 0 }}>
          {problem.summary}
        </p>
      )}
      {humanApproved && humanApproved.policy === "ask" && problem.decision.floor && (
        <TeachRule action={humanApproved} floor={problem.decision.floor} />
      )}
      {followUps.length > 0 && (
        <div className="box plain pad">
          <p>
            <b>Waiting for you</b>
          </p>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {followUps.map((a) => (
              <li key={a.id}>
                {a.title}
                {a.kind === "email_customers" ? " (not sent)" : ""}
              </li>
            ))}
          </ul>
        </div>
      )}
      <Timeline items={timeline} />
    </div>
  );
}

function TeachRule({ action, floor }: { action: Action; floor: FloorKind }) {
  const { api } = useDeps();
  const [st, setSt] = useState<"idle" | "saving" | "saved" | "kept" | { error: string }>("idle");
  const what = TEACH_PHRASE[action.kind] ?? KIND_VERB[action.kind];

  async function learn() {
    setSt("saving");
    try {
      await api.setRule({ kind: action.kind, mode: "alone", when: { floor: [floor] } });
      setSt("saved");
    } catch (e) {
      setSt({ error: e instanceof ApiError ? e.message : "We couldn't save that rule. Try again." });
    }
  }

  if (st === "saved")
    return (
      <div className="ask" role="status">
        <p>
          <b>
            Done. Next time your helper will {what} alone when {FLOOR_PHRASE[floor]}.
          </b>
        </p>
        <p className="muted">
          A message, not a call. Change it in <Link to="/rules">Helper rules</Link>.
        </p>
      </div>
    );
  if (st === "kept")
    return (
      <div className="ask" role="status">
        <p className="muted" style={{ margin: 0 }}>
          OK. Your helper will keep asking you first.
        </p>
      </div>
    );
  return (
    <section className="ask" aria-labelledby="teach-h">
      <p id="teach-h">
        <b>Next time, can your helper {what} without waking you?</b>
      </p>
      <p className="muted">
        Only when {FLOOR_PHRASE[floor]}
        {action.kind === "undo_update" ? " right after an update" : ""}. A message, not a call.
      </p>
      <div className="btn-row">
        <button type="button" className="btn" onClick={learn} disabled={st === "saving"}>
          {st === "saving" ? "Saving…" : "Yes, do it alone next time"}
        </button>
        <button type="button" className="btn alt" onClick={() => setSt("kept")} disabled={st === "saving"}>
          Keep asking me
        </button>
      </div>
      {typeof st === "object" && (
        <p className="err" role="alert">
          {st.error}
        </p>
      )}
    </section>
  );
}
